// modal.js
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import "./modal.css";
import {
  useAddTransactionMutation,
} from "../api/transactionsApi";
import { useTranslation } from "../hooks/useTranslation";
import { getApiErrorMessage } from "../api/errorUtils";

const AddTransactionModal = ({
  modalOpen,
  toggleModal,
  itemType,
  addNewItem,
  setTableData,
  editItemData = {},
  setEditItemData,
  notificationComponentRef,
  tableData,
  transactionsType = [],
}) => {
  const t = useTranslation();
  const [addTransaction] = useAddTransactionMutation();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize default values when modal opens or when transactionsType loads
  useEffect(() => {
    if (modalOpen) {
      // Set defaults for new transactions or if required fields are missing
      const needsDefaults =
        !editItemData.id ||
        !editItemData.subType ||
        !editItemData.transaction_type ||
        !editItemData.payment_type;

      if (needsDefaults) {
        const defaultValues = {
          ...editItemData, // Preserve any existing editItemData values first
          subType:
            editItemData.subType ||
            (transactionsType.length > 0 ? transactionsType[0].id : ""),
          transaction_type: editItemData.transaction_type || "outcome",
          payment_type: editItemData.payment_type || "cash",
          sale_price: editItemData.sale_price || "",
          description: editItemData.description || "",
        };
        setEditItemData(defaultValues);
      }
      setErrors({});
    }
  }, [modalOpen, transactionsType.length]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...editItemData, [name]: value };
    setEditItemData(updatedData);
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!editItemData.subType) {
      newErrors.subType =
        t("transactions.subTypeRequired") || "Transaction subtype is required";
    }

    if (!editItemData.transaction_type) {
      newErrors.transaction_type =
        t("transactions.transactionTypeRequired") ||
        "Transaction type is required";
    }

    if (!editItemData.payment_type) {
      newErrors.payment_type =
        t("transactions.paymentTypeRequired") || "Payment type is required";
    }

    if (!editItemData.sale_price || editItemData.sale_price <= 0) {
      newErrors.sale_price =
        t("transactions.priceRequired") || "Price must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      if (notificationComponentRef?.current) {
        notificationComponentRef.current.showNotification(
          t("transactions.validationError") ||
            "Please fill in all required fields correctly",
          "danger"
        );
      }
      return;
    }

    try {
      setLoading(true);

      // Prepare data for backend - ensure all required fields are present
      const transactionData = {
        subType: editItemData.subType,
        transaction_type: editItemData.transaction_type,
        payment_type: editItemData.payment_type,
        sale_price: parseFloat(editItemData.sale_price),
        description: editItemData.description || "",
      };

      // If editing, include the id
      if (editItemData.id) {
        transactionData.id = editItemData.id;
      }

      if (!editItemData.id) {
        // Adding new transaction
        const result = await addTransaction(transactionData).unwrap();
        const newObj = { id: result?.data || result?.id, ...transactionData };
        addNewItem(newObj);

        // Show success notification
        if (notificationComponentRef?.current) {
          notificationComponentRef.current.showNotification(
            t("transactions.transactionAdded") ||
              "Transaction added successfully",
            "success"
          );
        }

        // Close modal (toggleModal in Transactions.js will reset editItemData)
        toggleModal();
        setErrors({});
      } else {
        notificationComponentRef?.current?.showNotification(
          t("transactions.editDeferred") ||
            "Transaction editing is not available on the Nest backend yet",
          "warning"
        );
      }
    } catch (error) {
      console.error("Error saving transaction:", error);
      if (notificationComponentRef?.current) {
        notificationComponentRef.current.showNotification(
          getApiErrorMessage(
            error,
            t("transactions.errorSaving") || "Error saving transaction",
            {
              403: "Only company managers, admins, or owners can add manual transactions.",
              501:
                t("transactions.editDeferred") ||
                "Transaction editing is not available on the Nest backend yet",
            }
          ),
          "danger"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className="add-item-modal" isOpen={modalOpen} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>
        {editItemData.id
          ? t("transactions.editTransaction") || "Edit Transaction"
          : t("transactions.addTransaction") || "Add New Transaction"}
      </ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label for="subType">
              {t("transactions.subType") || "Transaction SubType"} *
            </Label>
            <Input
              type="select"
              name="subType"
              id="subType"
              value={editItemData.subType || ""}
              onChange={handleInputChange}
              invalid={!!errors.subType}
            >
              <option value="">
                {t("transactions.selectSubType") || "Select SubType"}
              </option>
              {transactionsType.map((transaction) => (
                <option key={transaction.id} value={transaction.id}>
                  {transaction.name}
                </option>
              ))}
            </Input>
            {errors.subType && (
              <div className="invalid-feedback d-block">{errors.subType}</div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="transaction_type">
              {t("transactions.transactionType") || "Transaction Type"} *
            </Label>
            <Input
              type="select"
              name="transaction_type"
              id="transaction_type"
              value={editItemData.transaction_type || ""}
              onChange={handleInputChange}
              invalid={!!errors.transaction_type}
            >
              <option value="">
                {t("transactions.selectTransactionType") || "Select Type"}
              </option>
              <option value="outcome">
                {t("transactions.outcome") || "Outcome"}
              </option>
              <option value="income">
                {t("transactions.income") || "Income"}
              </option>
            </Input>
            {errors.transaction_type && (
              <div className="invalid-feedback d-block">
                {errors.transaction_type}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="payment_type">
              {t("transactions.paymentType") || "Payment Type"} *
            </Label>
            <Input
              type="select"
              name="payment_type"
              id="payment_type"
              value={editItemData.payment_type || ""}
              onChange={handleInputChange}
              invalid={!!errors.payment_type}
            >
              <option value="">
                {t("transactions.selectPaymentType") || "Select Payment Type"}
              </option>
              <option value="bank">{t("transactions.bank") || "Bank"}</option>
              <option value="cash">{t("transactions.cash") || "Cash"}</option>
            </Input>
            {errors.payment_type && (
              <div className="invalid-feedback d-block">
                {errors.payment_type}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="sale_price">
              {t("transactions.price") || "Price"} *
            </Label>
            <Input
              type="number"
              name="sale_price"
              id="sale_price"
              value={editItemData.sale_price || ""}
              placeholder={t("transactions.enterPrice") || "Enter Price"}
              onChange={handleInputChange}
              min="0"
              step="0.01"
              invalid={!!errors.sale_price}
            />
            {errors.sale_price && (
              <div className="invalid-feedback d-block">
                {errors.sale_price}
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="description">
              {t("transactions.description") || "Description"}
            </Label>
            <Input
              type="text"
              name="description"
              id="description"
              value={editItemData.description || ""}
              placeholder={
                t("transactions.addDescription") || "Add Description"
              }
              onChange={handleInputChange}
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleSubmit} disabled={loading}>
          {loading
            ? t("common.saving") || "Saving..."
            : t("common.save") || "Save"}
        </Button>
        <Button color="secondary" onClick={toggleModal} disabled={loading}>
          {t("common.cancel") || "Cancel"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddTransactionModal;
